import React from 'react';
import { Alert, Typography, Divider } from 'antd';
import { CloseCircleOutlined, WarningOutlined, InfoCircleOutlined } from '@ant-design/icons';

const { Text, Paragraph } = Typography;

/**
 * VerificationErrorAlert
 *
 * Displays detailed verification error messages from the strict backend validation.
 * Parses multiline error text and presents it in a structured, user-friendly format.
 *
 * Props:
 *  - error: object with { message, details, type }
 *    - message: short error title
 *    - details: multiline detailed error text from backend
 *    - type: 'CRITICAL_MISMATCH' | 'TOTAL_MISMATCH' | 'UNKNOWN'
 */
const VerificationErrorAlert = ({ error }) => {
  if (!error || !error.details) return null;

  // Parse the error details into structured sections
  const parseErrorDetails = (detailsText) => {
    const lines = detailsText.split('\n');
    const result = {
      title: '',
      items: [],
      notes: [],
      action: '',
      raw: detailsText
    };

    let currentSection = '';

    for (let line of lines) {
      line = line.trim();
      if (!line) continue;

      if (line.includes('BILL VERIFICATION BLOCKED')) {
        result.title = 'Bill Verification Blocked';
        currentSection = 'intro';
      } else if (line.includes('BILL TOTAL VALIDATION FAILED')) {
        result.title = 'Bill Total Validation Failed';
        currentSection = 'intro';
      } else if (line.match(/^\d+\./)) {
        // Numbered item (e.g., "1. Item ID 101...")
        result.items.push(line);
        currentSection = 'items';
      } else if (line.startsWith('•')) {
        // Bullet point
        result.items.push(line.substring(1).trim());
      } else if (line.includes('Note:')) {
        currentSection = 'notes';
        result.notes.push(line.replace('Note:', '').trim());
      } else if (line.includes('Action:') || line.includes('Action Required:')) {
        result.action = line.replace('Action:', '').replace('Action Required:', '').trim();
      } else if (currentSection === 'notes') {
        result.notes.push(line);
      } else if (currentSection === 'items' && !line.match(/^Verification requires/)) {
        result.items.push(line);
      }
    }

    return result;
  };

  const parsed = parseErrorDetails(error.details);
  const isCritical = error.type === 'CRITICAL_MISMATCH' || error.type === 'TOTAL_MISMATCH';

  return (
    <Alert
      message={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isCritical ? <CloseCircleOutlined /> : <WarningOutlined />}
          <Text strong style={{ fontSize: 16 }}>
            {parsed.title || error.message || 'Verification Failed'}
          </Text>
        </div>
      }
      description={
        <div style={{ marginTop: 12 }}>
          {/* Issues Found */}
          {parsed.items.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>
                Issues Found:
              </Text>
              <ul style={{
                margin: 0,
                paddingLeft: 20,
                listStyleType: 'none'
              }}>
                {parsed.items.map((item, idx) => (
                  <li
                    key={idx}
                    style={{
                      marginBottom: 8,
                      padding: 10,
                      background: '#fff1f0',
                      border: '1px solid #ffa39e',
                      borderRadius: 4,
                      fontSize: 13
                    }}
                  >
                    <Text code style={{ fontSize: 12, wordBreak: 'break-word' }}>
                      {item}
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Additional Notes */}
          {parsed.notes.length > 0 && (
            <div style={{
              marginBottom: 16,
              padding: 12,
              background: '#fffbe6',
              border: '1px solid #ffe58f',
              borderRadius: 4
            }}>
              <div style={{ display: 'flex', alignItems: 'start', gap: 8 }}>
                <InfoCircleOutlined style={{ color: '#faad14', marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>
                    Additional Information:
                  </Text>
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13 }}>
                    {parsed.notes.map((note, idx) => (
                      <li key={idx} style={{ marginBottom: 4 }}>
                        <Text>{note}</Text>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Action Required */}
          {parsed.action && (
            <div style={{
              padding: 12,
              background: '#e6f7ff',
              border: '1px solid #91d5ff',
              borderRadius: 4,
              marginBottom: 12
            }}>
              <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>
                What to do:
              </Text>
              <Text style={{ fontSize: 13 }}>{parsed.action}</Text>
            </div>
          )}

          <Divider style={{ margin: '12px 0' }} />

          {/* Help Text */}
          <Paragraph style={{ fontSize: 12, margin: 0, color: '#595959' }}>
            <Text strong>Options:</Text> Review the bill data and correct errors, or reject this bill
            and request a corrected invoice from the vendor. The system now enforces strict matching to prevent
            overbilling, overcharging, tax mismatches, and calculation errors.
          </Paragraph>
        </div>
      }
      type="error"
      showIcon={false}
      style={{ marginBottom: 20 }}
    />
  );
};

export default VerificationErrorAlert;
